// src/App.js - FIXED VERSION
// ✅ Restored correct auth routes: /login and /signup (NOT /auth/login)
// ✅ Fixed routing structure

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Website Pages
import WebsitePage from './pages/website';
import AiMarketing from './pages/website/solutions/AiMarketing';
import AboutUs from './pages/website/company/about';
import Careers from './pages/website/company/careers';
import Contact from './pages/website/company/contact';
import PricingSection from './pages/website/company/pricingsection';

// Legal Pages
import PrivacyPolicy from './pages/website/legal/PrivacyPolicy';
import TermsOfService from './pages/website/legal/TermsOfService';

// Resource Pages
import ArticlesPage from './pages/website/resources/articles/ArticlesPage';
import BlogsPage from './pages/website/resources/blogs/BlogsPage';
import AiMarketingAutomation from './pages/website/resources/articles/AiMarketingAutomation';
import AiLeadGeneration from './pages/website/resources/articles/AiLeadGeneration';
import StopWastingTime from './pages/website/resources/blogs/StopWastingTime';
import IntelligentCrm from './pages/website/resources/blogs/IntelligentCrm';

// Authentication Pages
import { Login, Signup, Validation } from './pages/auth';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Application Pages
import DashboardPage from './pages/application/DashboardPage';
import CreateEventPage from './pages/application/CreateEventPage';
import DiscoverEventsPage from './pages/application/DiscoverEventsPage';
import CustomerPage from './pages/application/CustomerPage';
import MarketingToolsPage from './pages/application/MarketingToolsPage';
import SubscriptionPage from './pages/application/SubscriptionPage';
import SubscribePage from './pages/application/SubscribePage'; // NEW: Initial subscription page
import LeadGenerationPage from './pages/application/LeadGenerationPage';
import LeadsPage from './pages/application/LeadsPage'; // NEW: CRM Page
import SettingsPage from './pages/application/SettingsPage'; // NEW: Settings Page
import ResultsPage from './pages/application/ResultsPage'; // NEW: Results & Social
import MediaUploadPage from './pages/MediaUploadPage'; // Media upload via email link

// Protected Route Component
import ProtectedRoute from './components/auth/ProtectedRoute';

// Scroll to Top Component
import ScrollToTop from './components/ScrollToTop';

// Instagram OAuth Callback
import InstagramCallback from './components/auth/InstagramCallback';

// Email Verification Components
const EmailVerificationPending = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-2xl font-bold mb-4">Check Your Email</h1>
      <p>Please check your email to verify your account.</p>
    </div>
  </div>
);

const EmailVerificationSuccess = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-2xl font-bold mb-4 text-green-600">Email Verified!</h1>
      <p>Your account has been verified successfully.</p>
      <a href="/dashboard" className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded">
        Go to Dashboard
      </a>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen bg-transparent flex flex-col">
          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#ffffff',
                color: '#1f2937',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#ffffff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
            }}
          />

          <Routes>
            {/* Authentication Routes - FIXED: /login and /signup (NOT /auth/login) */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/validation" element={<Validation />} />
            
            {/* Legacy /auth routes for backward compatibility */}
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/signup" element={<Signup />} />
            <Route path="/auth/validation" element={<Validation />} />
            <Route path="/auth" element={<Login />} />
            
            {/* Password Reset Routes */}
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password/:token" element={<ResetPassword />} />

            {/* Email Verification Routes */}
            <Route path="/auth/verify-email-pending" element={<EmailVerificationPending />} />
            <Route path="/auth/verify-email/:token" element={<EmailVerificationSuccess />} />

            {/* Instagram OAuth Callback */}
            <Route
              path="/api/strategy/auth/instagram/callback"
              element={<InstagramCallback />}
            />

            {/* Media Upload Route - Public (token-based auth) */}
            <Route path="/upload/:uploadToken" element={<MediaUploadPage />} />

            {/* Dashboard Area Routes (Sidebar Layout - No Header/Footer) */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/create-event" 
              element={
                <ProtectedRoute>
                  <CreateEventPage />
                </ProtectedRoute>
              } 
            />
            
            {/* UPDATED: Customers route now points to LeadsPage (CRM) */}
            <Route 
              path="/customers" 
              element={
                <ProtectedRoute>
                  <LeadsPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Keep old CustomerPage accessible if needed */}
            <Route 
              path="/customers-old" 
              element={
                <ProtectedRoute>
                  <CustomerPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/marketing" 
              element={
                <ProtectedRoute>
                  <MarketingToolsPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <SubscriptionPage />
                </ProtectedRoute>
              }
            />

            {/* Initial Subscription Page - Shown after registration */}
            <Route
              path="/subscribe"
              element={
                <ProtectedRoute>
                  <SubscribePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/results"
              element={
                <ProtectedRoute>
                  <ResultsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lead-generation"
              element={
                <ProtectedRoute>
                  <LeadGenerationPage />
                </ProtectedRoute>
              }
            />

            {/* Solutions Routes (No Header/Footer - have their own) */}
            <Route path="/solutions/ai-marketing" element={<AiMarketing />} />

            {/* Public Routes with Header/Footer */}
            <Route
              path="/*"
              element={
                <>
                  <Header />
                  <main className="flex-1">
                    <Routes>
                      <Route path="/" element={<WebsitePage />} />
                      <Route path="/events" element={<DiscoverEventsPage />} />
                      <Route path="/company/about" element={<AboutUs />} />
                      <Route path="/company/careers" element={<Careers />} />
                      <Route path="/company/contact" element={<Contact />} />
                      <Route path="/company/pricingsection" element={<PricingSection />} />

                      {/* Legal Pages */}
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/terms-of-service" element={<TermsOfService />} />

                      {/* Resource Pages */}
                      <Route path="/resources/articles" element={<ArticlesPage />} />
                      <Route path="/resources/blogs" element={<BlogsPage />} />
                      <Route path="/resources/articles/ai-marketing-automation" element={<AiMarketingAutomation />} />
                      <Route path="/resources/articles/ai-lead-generation-guide" element={<AiLeadGeneration />} />
                      <Route path="/resources/blogs/stop-wasting-time-marketing" element={<StopWastingTime />} />
                      <Route path="/resources/blogs/intelligent-crm-small-business" element={<IntelligentCrm />} />

                      <Route
                        path="*"
                        element={
                          <div className="min-h-screen flex items-center justify-center">
                            <div className="text-center">
                              <h1 className="text-4xl font-bold mb-4">404</h1>
                              <p className="mb-4">Page not found</p>
                              <a href="/" className="bg-blue-600 text-white px-4 py-2 rounded">
                                Go Home
                              </a>
                            </div>
                          </div>
                        }
                      />
                    </Routes>
                  </main>
                  <Footer />
                </>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;